import { Chip, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { MimeTypeHelper } from "@/common/helpers/mimeType";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { DataExportConfigDto, DataExportConfigGetPaginatedDto } from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";

export type DataExportConfigAutocompleteOption = BaseAutocompleteOption<DataExportConfigDto>;

export const getDataExportConfigTitle = (data: DataExportConfigDto): string => data.identifier!;

export const dataExportConfigToOption = (
  data: DataExportConfigDto,
): DataExportConfigAutocompleteOption => ({
  id: data.id!,
  title: getDataExportConfigTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DataExportConfigAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DataExportConfigDto> {
  searchFilters?: Partial<Omit<DataExportConfigGetPaginatedDto, "search" | "includeIds">>;
}

export default function DataExportConfigAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DataExportConfigAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={dataExportConfigToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.dataExportApi.apiV1DataExportConfigsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          dataExportConfigGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          dataExportConfigGetPaginatedDto: {
            ...params.dataExportConfigGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Data Export Config'
      placeholder='Search...'
      renderOption={(props, option: DataExportConfigAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              <AppIcon of='export' />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                    sx={{ mb: 1 }}
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Stack direction='row' spacing={1}>
                      {option.data.type && (
                        <Chip
                          label={<InlineApiEnumValue type='ExportType' value={option.data.type} />}
                        />
                      )}
                      {option.data.mimeType && (
                        <Chip
                          label={
                            MimeTypeHelper.getFileExtensionByMimeType(option.data.mimeType) ||
                            option.data.mimeType
                          }
                        />
                      )}
                    </Stack>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
